/**
 * Client-Side Implementation FPTI using pa.js
 * Documentation: https://github.paypal.com/pages/Tracking-R/client-side-instrumentation/docs/index.html
 */
import {
  CLICK,
  IMPRESSION,
  SHOPPING_MERCHANT_SCREEN_SHOWN,
  SHOPPING_MERCHANT_SITE_PRESSED,
  SHOPPING_MERCHANT_TAB_ALL_PRESSED,
  SHOPPING_MERCHANT_TAB_CODES_PRESSED,
  SHOPPING_MERCHANT_TAB_DEALS_PRESSED,
  SHOPPING_MERCHANT_DEAL_PRESSED,
  SHOPPING_MERCHANT_CODE_PRESSED,
  SHOPPING_MERCHANT_CODE_SCREEN_SHOWN,
  SHOPPING_MERCHANT_CODE_COPY_PRESSED,
  SHOPPING_MERCHANT_CODE_USE_PRESSED,
  SHOPPING_MERCHANT_CODE_CLOSE_PRESSED,
  SHOPPING_MERCHANT_DEAL_USE_PRESSED,
  OFFER_DETAIL_CTA_CLICKED,
  OFFER_DETAIL_DISMISS_CLICKED,
  OFFER_DETAIL_SCREEN_SHOWN
} from './client-constants'

export type FptiEventNames =
  | typeof SHOPPING_MERCHANT_SCREEN_SHOWN
  | typeof SHOPPING_MERCHANT_SITE_PRESSED
  | typeof SHOPPING_MERCHANT_TAB_ALL_PRESSED
  | typeof SHOPPING_MERCHANT_TAB_CODES_PRESSED
  | typeof SHOPPING_MERCHANT_TAB_DEALS_PRESSED
  | typeof SHOPPING_MERCHANT_DEAL_PRESSED
  | typeof SHOPPING_MERCHANT_CODE_PRESSED
  | typeof SHOPPING_MERCHANT_CODE_SCREEN_SHOWN
  | typeof SHOPPING_MERCHANT_CODE_COPY_PRESSED
  | typeof SHOPPING_MERCHANT_CODE_USE_PRESSED
  | typeof SHOPPING_MERCHANT_CODE_CLOSE_PRESSED
  | typeof OFFER_DETAIL_CTA_CLICKED
  | typeof OFFER_DETAIL_DISMISS_CLICKED
  | typeof OFFER_DETAIL_SCREEN_SHOWN
  | typeof SHOPPING_MERCHANT_DEAL_USE_PRESSED

export interface FptiDataType {
  event_name: FptiEventNames
  [key: string]: any
}

type OptionalEventData = {
  [key: string]: string
}

export type FptiClick = (
  eventName: FptiEventNames,
  optionalEventData?: OptionalEventData
) => void

// Prevent the App to crash if PAYPAL is not loaded correctly
const isPayPalAnalyticsLoaded = (): boolean => {
  if (window.hasOwnProperty('PAYPAL') && PAYPAL.hasOwnProperty('analytics')) {
    return true
  }
  return false
}

/**
 * Documentation: https://github.paypal.com/pages/Tracking-R/client-side-instrumentation/docs/index.html
 * @param data For data payload, refer to FPTI dictionary at https://go/fptigov
 * @param isContextAware Should the data payload be merged with window.fpti object? By default it's true
 */
export const sendFptiClick = (
  data: FptiDataType,
  isContextAware = true
): void => {
  if (!isPayPalAnalyticsLoaded()) {
    return
  }
  PAYPAL.analytics.instance.recordEvent(
    data.event_name,
    { ...data, e: CLICK },
    isContextAware
  )
}

/**
 * Documentation: https://github.paypal.com/pages/Tracking-R/client-side-instrumentation/docs/index.html
 * @param data For data payload, refer to FPTI dictionary at https://go/fptigov
 * @param isContextAware Should the data payload be merged with window.fpti object? By default it's true
 */
export const sendFptiImpression = (
  data: FptiDataType,
  isContextAware = true as boolean
): void => {
  if (!isPayPalAnalyticsLoaded()) {
    return
  }
  PAYPAL.analytics.instance.recordEvent(
    data.event_name,
    { ...data, e: IMPRESSION },
    isContextAware
  )
}
